import { err, ok, Result } from 'neverthrow';
import { lex } from './lex';
import type {
  WithMeta,
  TokenMeta,
  WithRange,
  IntermediateToken,
} from './types';
import { Token } from './tokens';
import { Assert } from '@cotera/utilities';

export class TokenStream {
  private stream: PeekableStream<WithMeta<IntermediateToken>>;

  constructor(
    private readonly str: string,
    opts?: { readonly offset?: number }
  ) {
    const tokenStream = lex(str, opts);
    this.stream = new PeekableStream(intermediateTokenStream(tokenStream));
  }

  currentOffset(): number {
    const next = this.peak();
    return next?.[1].range?.[0] ?? this.str.length;
  }

  debug(): { next: WithMeta<Token> | null; split: readonly [string, string] } {
    const next = this.peak();
    const info = {
      next,
      split: [
        this.originalForMeta({
          range: [0, this.currentOffset()],
        }),
        this.originalForMeta({
          range: [this.currentOffset(), this.str.length],
        }),
      ] as const,
    };
    return info;
  }

  originalForMeta({ range }: Pick<TokenMeta, 'range'>): string {
    return range ? this.str?.slice(range[0], range[1]) ?? '' : '';
  }

  consumeIf(cb: (x: Token) => boolean): Result<WithMeta<Token>, null | false> {
    const next = this.peak();

    if (next === null) {
      return err(null);
    }

    return cb(next[0]) ? ok(this.consume()!) : err(false);
  }

  peak() {
    return this.stream.peek();
  }

  consume(): WithMeta<IntermediateToken> | null {
    return this.stream.consume();
  }

  isFinished(): boolean {
    return this.peak() === null;
  }
}

class PeekableStream<T> {
  buffer: T[] = [];
  constructor(private stream: Generator<T>) {}

  consume(): T | null {
    const buff = this.buffer.shift();

    if (buff !== undefined) {
      return buff;
    }

    const next = this.stream.next();

    if (next.done) {
      return null;
    }

    return next.value;
  }

  peek(): T | null {
    const buffPeek = this.buffer[0];

    if (buffPeek === undefined) {
      return this.loadBuffer();
    }

    return buffPeek;
  }

  private loadBuffer(): T | null {
    const next = this.stream.next();

    if (next.done) {
      return null;
    }

    this.buffer.push(next.value);
    return next.value;
  }
}

function* intermediateTokenStream(
  tokenStream: Generator<WithRange<Token>>
): Generator<WithMeta<IntermediateToken>, void> {
  const nonWhitespaceTokenStream = new PeekableStream(
    skipWhitespaces(tokenStream)
  );

  const leadingComments = [];

  while (true) {
    const next = nonWhitespaceTokenStream.consume();

    if (next === null) {
      return;
    }

    const [token, { range }] = next;

    if (token.t === 'comment') {
      Assert.assert(
        token.direction === 'leading',
        "Comments that don't come after a non-comment/non-whitespace token must be leading"
      );

      leadingComments.push(token.contents);

      continue;
    }

    // Collect comments
    const peek = nonWhitespaceTokenStream.peek();
    let trailing: string | null = null;
    if (peek && peek[0].t === 'comment' && peek[0].direction === 'trailing') {
      nonWhitespaceTokenStream.consume();
      trailing = peek[0].contents;
    }
    const leading = [...leadingComments];
    leadingComments.length = 0;

    yield [
      token,
      {
        range,
        comments: {
          leading,
          trailing,
        },
      },
    ];
  }
}

function* skipWhitespaces(
  tokenStream: Generator<WithRange<Token>>
): Generator<WithRange<NonWhitespaceToken>, void> {
  while (true) {
    const next = tokenStream.next();

    if (next.done) {
      return;
    }

    const [token, range] = next.value;

    if (token.t === 'white-space') {
      continue;
    }

    yield [token, range];
  }
}

type NonWhitespaceToken = Exclude<Token, { t: 'whitespace' }>;

export function listOfTokenStream(
  stream: TokenStream
): WithMeta<IntermediateToken>[] {
  const res = [];

  while (!stream.isFinished()) {
    const next = Assert.assertExists(stream.consume());
    res.push(next);
  }

  return res;
}
